/**
 * Teacher-facing presentation mapper for Malay formative skill evidence.
 *
 * Presentation only — does not change evidence eligibility, scoring, or API data.
 * Qualifying evidence continues to use formativeSkillCountsAsEvidence().
 */
import type { FormativeActivityDetail } from '../../../modules/tutor/api/malayFormativeProgress.client';
import { humanizeSkillKey } from '../tutor-analytics/tutorAnalyticsDisplayUtils';
import { formativeSkillCountsAsEvidence } from './teacherMalayLearningActivitiesUtils';

export type FormativeSkillRow = FormativeActivityDetail['skills'][number];

export type TeacherEvidenceStatusKind = 'quality' | 'confirmation_only' | 'not_enough';

export type TeacherSkillEvidenceView = {
  skillKey: string;
  qualifiesAsEvidence: boolean;
  isConfirmationOnly: boolean;
  /** Backend level when it may be shown; null means display em dash. */
  displayLevel: string | null;
  showLevelAsterisk: boolean;
  evidenceStatusKind: TeacherEvidenceStatusKind;
  observationCount: number;
  showObservationCount: boolean;
  latestEvidenceAt: string | null;
  assessmentKind: 'aiEvaluated' | 'confirmedText' | 'confirmation' | null;
};

export type TeacherSkillLevelOverview = {
  strong: number;
  secure: number;
  developing: number;
  beginning: number;
  confirmationOnly: number;
  notEnoughEvidence: number;
};

const CONFIRMATION_QUALITY = new Set(['confirmation_only', 'confirmation_progress_proxy']);

/** i18n path for a catalogue skill_key (matches backend skills.py SKILL_DISPLAY). */
export function formativeSkillLabelI18nKey(skillKey: string): string | null {
  const trimmed = skillKey.trim();
  const dot = trimmed.indexOf('.');
  if (dot <= 0 || dot === trimmed.length - 1) return null;
  const ns = trimmed.slice(0, dot);
  const rest = trimmed.slice(dot + 1);
  if (ns !== 'reading' && ns !== 'narrative' && ns !== 'unit5') return null;
  if (!rest || rest.includes('.')) return null;
  return `dashboard.malayFormative.skillLabels.${ns}.${rest}`;
}

export function resolveFormativeSkillLabel(skillKey: string, t: (key: string) => string): string {
  const i18nKey = formativeSkillLabelI18nKey(skillKey);
  if (i18nKey) {
    const translated = t(i18nKey);
    if (translated && translated !== i18nKey) return translated;
  }
  return humanizeSkillKey(skillKey);
}

export function formativeSkillIsConfirmationOnly(skill: FormativeSkillRow): boolean {
  if (formativeSkillCountsAsEvidence(skill)) return false;
  if (!skill.current_level || skill.current_level === 'not_enough_evidence') return false;
  if ((skill.valid_observation_count ?? 0) <= 0) return false;
  if (skill.counts_as_skill_evidence === false) return true;
  const quality = String(skill.evidence_quality || '').toLowerCase();
  return CONFIRMATION_QUALITY.has(quality);
}

export function teacherEvidenceStatusKind(skill: FormativeSkillRow): TeacherEvidenceStatusKind {
  if (formativeSkillCountsAsEvidence(skill)) return 'quality';
  if (formativeSkillIsConfirmationOnly(skill)) return 'confirmation_only';
  return 'not_enough';
}

export function latestObservationIsoForSkill(
  skillKey: string,
  observations?: Array<Record<string, unknown>> | null,
): string | null {
  let latest: string | null = null;
  for (const obs of observations ?? []) {
    if (String(obs.skill_key ?? '') !== skillKey) continue;
    const at = String(obs.created_at ?? '').trim();
    if (!at) continue;
    if (!latest || at >= latest) latest = at;
  }
  return latest;
}

export function formatTeacherEvidenceDate(iso: string | null | undefined, locale?: string): string | null {
  if (!iso) return null;
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return null;
  try {
    return d.toLocaleDateString(locale || undefined, {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
    });
  } catch {
    return iso;
  }
}

function assessmentKindForSkill(
  skill: FormativeSkillRow,
): TeacherSkillEvidenceView['assessmentKind'] {
  const quality = String(skill.evidence_quality || '').toLowerCase();
  const type = String(skill.latest_evidence_type || '').toLowerCase();
  const basis = String(skill.assessment_basis || '').toLowerCase();
  if (
    quality === 'confirmation_only' ||
    quality === 'confirmation_progress_proxy' ||
    type === 'learner_confirmed_plot' ||
    basis === 'confirmation_only' ||
    basis === 'confirmation_progress_proxy'
  ) {
    return 'confirmation';
  }
  if (basis === 'confirmed_text_quality' || quality === 'confirmed_text_quality') {
    return 'confirmedText';
  }
  if (type === 'learner_response' || quality === 'quality_or_heuristic') {
    return 'aiEvaluated';
  }
  if (formativeSkillCountsAsEvidence(skill)) return 'aiEvaluated';
  return null;
}

export function mapSkillToTeacherEvidenceView(
  skill: FormativeSkillRow,
  observations?: Array<Record<string, unknown>> | null,
): TeacherSkillEvidenceView {
  const qualifiesAsEvidence = formativeSkillCountsAsEvidence(skill);
  const isConfirmationOnly = formativeSkillIsConfirmationOnly(skill);
  const evidenceStatusKind = teacherEvidenceStatusKind(skill);
  const observationCount = Number(skill.valid_observation_count ?? 0) || 0;
  const scoredLevel =
    skill.current_level && skill.current_level !== 'not_enough_evidence' ? skill.current_level : null;

  return {
    skillKey: skill.skill_key,
    qualifiesAsEvidence,
    isConfirmationOnly,
    displayLevel: qualifiesAsEvidence || isConfirmationOnly ? scoredLevel : null,
    showLevelAsterisk: isConfirmationOnly,
    evidenceStatusKind,
    observationCount,
    showObservationCount: observationCount > 0 && (qualifiesAsEvidence || isConfirmationOnly),
    latestEvidenceAt: latestObservationIsoForSkill(skill.skill_key, observations),
    assessmentKind: assessmentKindForSkill(skill),
  };
}

export function formativeSkillLevelOverview(skills: FormativeSkillRow[] | undefined): TeacherSkillLevelOverview {
  const overview: TeacherSkillLevelOverview = {
    strong: 0,
    secure: 0,
    developing: 0,
    beginning: 0,
    confirmationOnly: 0,
    notEnoughEvidence: 0,
  };
  for (const skill of skills ?? []) {
    if (formativeSkillIsConfirmationOnly(skill)) {
      overview.confirmationOnly += 1;
      continue;
    }
    if (!formativeSkillCountsAsEvidence(skill)) {
      overview.notEnoughEvidence += 1;
      continue;
    }
    switch (skill.current_level) {
      case 'strong':
        overview.strong += 1;
        break;
      case 'secure':
        overview.secure += 1;
        break;
      case 'developing':
        overview.developing += 1;
        break;
      case 'beginning':
        overview.beginning += 1;
        break;
      default:
        overview.notEnoughEvidence += 1;
    }
  }
  return overview;
}

const STATUS_SORT: Record<TeacherEvidenceStatusKind, number> = {
  quality: 0,
  confirmation_only: 1,
  not_enough: 2,
};

const LEVEL_SORT: Record<string, number> = {
  strong: 0,
  secure: 1,
  developing: 2,
  beginning: 3,
};

/** Sort for teacher scanning — does not change evidence values. */
export function sortSkillsForTeacherPresentation(skills: FormativeSkillRow[]): FormativeSkillRow[] {
  return [...skills].sort((a, b) => {
    const statusDiff = STATUS_SORT[teacherEvidenceStatusKind(a)] - STATUS_SORT[teacherEvidenceStatusKind(b)];
    if (statusDiff !== 0) return statusDiff;
    const levelA = LEVEL_SORT[a.current_level || ''] ?? 9;
    const levelB = LEVEL_SORT[b.current_level || ''] ?? 9;
    if (levelA !== levelB) return levelA - levelB;
    return a.skill_key.localeCompare(b.skill_key);
  });
}

const RAW_INTERNAL_TERMS = [
  'confirmation_only',
  'confirmation_progress_proxy',
  'counts_as_skill_evidence',
  'evidence_quality',
  'assessment_basis',
  'latest_evidence_type',
  'latest_reason',
  'valid_observation_count',
  'current_level',
  'skill_key',
  'learner_confirmed_plot',
  'quality_or_heuristic',
  'confirmed_text_quality',
  'not_enough_evidence',
];

/** True when teacher-facing copy still contains internal enum/field names. */
export function teacherPrimaryUiExposesRawTerms(text: string): boolean {
  const haystack = text.toLowerCase();
  return RAW_INTERNAL_TERMS.some((term) => haystack.includes(term.toLowerCase()));
}

/** Narrative plot-gate steps that map onto skills — display labels only. */
const NARRATIVE_PLOT_STEPS = new Set([
  'idea_utama',
  'tajuk',
  'watak_utama',
  'permulaan_tempat',
  'permulaan_masa',
  'permulaan_penemuan',
  'konflik',
  'peristiwa_peristiwa',
  'klimaks',
  'peleraian',
  'pengajaran',
]);

export type TeacherEvidenceRecord = {
  skillKey: string;
  masteryLevel: string | null;
  excerpt: string;
  reason: string;
  plotStep: string | null;
  createdAt: string | null;
  evidenceTurnId: string | null;
  evidenceType: string | null;
};

export type TeacherSkillEvidenceGroup = {
  skillKey: string;
  recordCount: number;
  latestAt: string | null;
  plotSteps: string[];
  records: TeacherEvidenceRecord[];
};

export function extractObservationPlotStep(obs: Record<string, unknown>): string | null {
  const meta = obs.metadata;
  if (meta && typeof meta === 'object') {
    const step = String((meta as { plot_step?: unknown }).plot_step ?? '').trim();
    if (NARRATIVE_PLOT_STEPS.has(step)) return step;
  }
  const turn = String(obs.evidence_turn_id ?? '');
  const colon = turn.lastIndexOf(':');
  if (colon >= 0) {
    const suffix = turn.slice(colon + 1).trim();
    if (NARRATIVE_PLOT_STEPS.has(suffix)) return suffix;
  }
  const reason = String(obs.reason ?? '');
  const quoted = reason.match(/plot step ['"]([^'"]+)['"]/i);
  if (quoted && NARRATIVE_PLOT_STEPS.has(quoted[1])) return quoted[1];
  return null;
}

export function formatPlotStepLabel(step: string): string {
  return step.replace(/_/g, ' ');
}

export function normalizeTeacherEvidenceRecord(
  raw: Record<string, unknown> | null | undefined,
): TeacherEvidenceRecord | null {
  if (!raw || typeof raw !== 'object') return null;
  const skillKey = String(raw.skill_key ?? '').trim();
  if (!skillKey) return null;
  return {
    skillKey,
    masteryLevel: raw.mastery_level ? String(raw.mastery_level) : null,
    excerpt: String(raw.safe_evidence_excerpt ?? '').trim(),
    reason: String(raw.reason ?? '').trim(),
    plotStep: extractObservationPlotStep(raw),
    createdAt: String(raw.created_at ?? '').trim() || null,
    evidenceTurnId: String(raw.evidence_turn_id ?? '').trim() || null,
    evidenceType: String(raw.evidence_type ?? '').trim() || null,
  };
}

function evidenceRecordIdentityKeys(record: TeacherEvidenceRecord): string[] {
  const keys: string[] = [];
  if (record.evidenceTurnId) {
    keys.push(`turn:${record.evidenceTurnId}|${record.skillKey}`);
  }
  keys.push(
    `soft:${record.skillKey}|${record.createdAt ?? ''}|${record.reason}|${record.masteryLevel ?? ''}`,
  );
  return keys;
}

function evidenceRecordToRaw(record: TeacherEvidenceRecord): Record<string, unknown> {
  return {
    skill_key: record.skillKey,
    mastery_level: record.masteryLevel,
    safe_evidence_excerpt: record.excerpt,
    reason: record.reason,
    created_at: record.createdAt,
    evidence_turn_id: record.evidenceTurnId,
    evidence_type: record.evidenceType,
    metadata: record.plotStep ? { plot_step: record.plotStep } : undefined,
  };
}

/** Merge learner + teacher observation lists without dropping distinct records. */
export function collectTeacherEvidenceRecords(
  detail: FormativeActivityDetail | null | undefined,
): TeacherEvidenceRecord[] {
  const teacherObs = Array.isArray(detail?.teacher_observations) ? detail.teacher_observations : [];
  const observations = Array.isArray(detail?.observations) ? detail.observations : [];
  const seen = new Set<string>();
  const out: TeacherEvidenceRecord[] = [];

  const add = (raw: Record<string, unknown>) => {
    const record = normalizeTeacherEvidenceRecord(raw);
    if (!record) return;
    const keys = evidenceRecordIdentityKeys(record);
    if (keys.some((key) => seen.has(key))) return;
    keys.forEach((key) => seen.add(key));
    out.push(record);
  };

  // Richer teacher rows first (excerpts / turn ids), then remaining learner rows.
  for (const raw of teacherObs) add(raw);
  for (const raw of observations) add(raw);
  return out;
}

export function groupEvidenceRecordsBySkill(
  records: TeacherEvidenceRecord[],
): TeacherSkillEvidenceGroup[] {
  const bySkill = new Map<string, TeacherEvidenceRecord[]>();
  for (const record of records) {
    const list = bySkill.get(record.skillKey) ?? [];
    list.push(record);
    bySkill.set(record.skillKey, list);
  }

  return [...bySkill.entries()].map(([skillKey, skillRecords]) => {
    const sorted = [...skillRecords].sort((a, b) => {
      const atA = a.createdAt ?? '';
      const atB = b.createdAt ?? '';
      if (atA !== atB) return atB.localeCompare(atA);
      return (a.plotStep ?? '').localeCompare(b.plotStep ?? '');
    });
    const plotStepOrder = [...NARRATIVE_PLOT_STEPS];
    const plotSteps: string[] = [];
    for (const record of sorted) {
      if (record.plotStep && !plotSteps.includes(record.plotStep)) {
        plotSteps.push(record.plotStep);
      }
    }
    plotSteps.sort((a, b) => plotStepOrder.indexOf(a) - plotStepOrder.indexOf(b));
    return {
      skillKey,
      recordCount: sorted.length,
      latestAt: sorted.find((record) => record.createdAt)?.createdAt ?? null,
      plotSteps,
      records: sorted,
    };
  });
}

export function evidenceGroupsBySkillKey(
  records: TeacherEvidenceRecord[],
): Record<string, TeacherSkillEvidenceGroup> {
  return Object.fromEntries(groupEvidenceRecordsBySkill(records).map((group) => [group.skillKey, group]));
}

export function collectSkillObservations(
  detail: FormativeActivityDetail | null | undefined,
): Array<Record<string, unknown>> {
  return collectTeacherEvidenceRecords(detail).map(evidenceRecordToRaw);
}
